import { PaylineGeometry } from '@/containers/winnings/PaylineGeometry'
import { PaylineMaterial } from '@/containers/winnings/PaylineMaterial'
import Facade from '@/Facade'
import { buildAssetUrl } from '@/utils/AssetsUrlUtil'
import * as PIXI from 'pixi.js'

export class Payline extends PIXI.Container {
    private static readonly ANIMATION_SPEED: number = 0.03
    private static readonly ANIMATION_SPEED_SLOW: number = 0.0025
    private static readonly SLOW_THRESHOLD: number = 0.99
    private static readonly SLOW_DURATION: number = 40
    private static _meshOffsetX: number = 0
    private static _meshOffsetY: number = 0

    private readonly _ropePoints: PIXI.Point[]
    private readonly _ropeGeometry: PaylineGeometry
    private readonly _ropeMesh: any

    private _progress: number
    private _isAnimated: boolean
    private _slowTime: number

    private static resolveWinlineTexture(): PIXI.Texture {
        const candidates = ['winline/winline', 'winline/winline.png', 'winline', 'winline.png']

        for (const key of candidates) {
            if (PIXI.Cache.has(key)) {
                return PIXI.Texture.from(key)
            }
        }

        // Safe fallback: keeps shader/geometry running even if atlas key is missing.
        // Fallback to a bundled local file so line texture is still available
        // even when spritesheet atlas keys are not yet rebuilt.
        return PIXI.Texture.from(buildAssetUrl('/assets/winline/winline.png'))
    }

    public constructor(sourcePoints: PIXI.Point[]) {
        super()

        this._ropePoints = sourcePoints
        this._ropeGeometry = new PaylineGeometry({ width: 75, points: this._ropePoints })

        const winlineTex = Payline.resolveWinlineTexture()
        this._ropeMesh = new PIXI.Mesh({
            geometry: this._ropeGeometry,
            shader: new PaylineMaterial(winlineTex),
        })

        this._ropeMesh.visible = false
        // Workspace has multiple Pixi instances in type space; cast avoids false-positive mismatch.
        ;(this as any).addChildAt(this._ropeMesh as any, 0)

        this._isAnimated = false
        this._progress = 0
        this._slowTime = 0
    }

    private onTickerUpdate = (ticker: any) => {
        const deltaTime = ticker.deltaTime
        let animationSpeed = Payline.ANIMATION_SPEED

        if (this._progress > Payline.SLOW_THRESHOLD) {
            if (this._slowTime < Payline.SLOW_DURATION) {
                animationSpeed = Payline.ANIMATION_SPEED_SLOW
                this._slowTime += deltaTime
            }
        }

        this._progress = Math.min(this._progress + deltaTime * animationSpeed, 2.0)
        this._ropeMesh.visible = false

        const currentRopePoints: PIXI.Point[] = []
        const firstIndex = 0
        const lastIndex = this._ropePoints.length - 1
        const startIndex = this._progress >= 1 ? Math.floor(lastIndex * (this._progress - 1)) : firstIndex
        const stopIndex = this._progress < 1 ? Math.floor(lastIndex * this._progress) : lastIndex
        const missingIndices = this._ropePoints.length - (stopIndex - startIndex)
        const currentRopeFading: number[] = []
        const fadeOffsets = 10

        if (this._progress < 1) {
            const startPoint = this._ropePoints[firstIndex]

            for (let index = 0; index < missingIndices; index++) {
                currentRopePoints.push(startPoint)
                currentRopeFading.push(0)
            }
        }

        for (let index = startIndex; index < stopIndex; index++) {
            currentRopePoints.push(this._ropePoints[index])

            if (index <= startIndex + fadeOffsets) {
                currentRopeFading.push((index - startIndex) / fadeOffsets)
            } else if (index >= stopIndex - fadeOffsets) {
                currentRopeFading.push((stopIndex - index) / fadeOffsets)
            } else {
                currentRopeFading.push(1)
            }
        }

        if (this._progress >= 1) {
            const stopPoint = this._ropePoints[lastIndex]

            for (let index = 0; index < missingIndices; index++) {
                currentRopePoints.push(stopPoint)
                currentRopeFading.push(0)
            }
        }

        if (currentRopePoints.length > 1) {
            this._ropeGeometry.points = currentRopePoints
            this._ropeGeometry.fading = currentRopeFading
            this._ropeGeometry.update()
            this._ropeMesh.visible = true
        }

        if (this._progress >= 2) {
            this.hide()
        }
    }

    public show(): void {
        this._slowTime = 0
        this._ropeMesh.visible = false
        this._progress = 0

        if (!this._isAnimated) {
            Facade.app.ticker.add(this.onTickerUpdate)
            this._isAnimated = true
        }
    }

    public showStatic(): void {
        this.hide()
        this._ropeGeometry.points = this._ropePoints
        this._ropeGeometry.fading = this._ropePoints.map(() => 1)
        this._ropeGeometry.update()
        this._ropeMesh.visible = true
    }

    public hide(): void {
        this._ropeMesh.visible = false

        if (this._isAnimated) {
            Facade.app.ticker.remove(this.onTickerUpdate)
            this._isAnimated = false
        }
    }

    public destroy(options?: PIXI.DestroyOptions): void {
        this.hide()
        super.destroy(options)
    }

    /**
     * Build the bezier rope points from raw [reel, row] positions,
     * replicating the same curve logic used in lobster's Payline.
     */
    public static buildRopePoints(rawPositions: number[][], symbolWidth: number, symbolHeight: number): PIXI.Point[] {
        // Sort by reel index so the line flows left→right
        const sorted = [...rawPositions].sort((a, b) => a[0] - b[0])

        const sourceLine = Payline.createSourceLine(sorted)
        const resultLine = Payline.createResultLine(sourceLine)
        const points = Payline.createRopePoints(resultLine, symbolWidth, symbolHeight)

        return points.map((point) => new PIXI.Point(point.x + Payline._meshOffsetX, point.y + Payline._meshOffsetY))
    }

    public static setMeshOffset(x: number, y: number): void {
        Payline._meshOffsetX = Number.isFinite(x) ? x : 0
        Payline._meshOffsetY = Number.isFinite(y) ? y : 0
    }

    private static createSourceLine(positions: number[][]): number[][] {
        const sourceLine = [...positions]
        const firstPoint = sourceLine[0]
        const lastPoint = sourceLine[sourceLine.length - 1]
        const offsetSize = 0.8

        sourceLine.unshift([firstPoint[0] - offsetSize, firstPoint[1]])
        sourceLine.push([lastPoint[0] + offsetSize, lastPoint[1]])

        return sourceLine
    }

    private static createResultLine(sourceLine: number[][]): number[][] {
        const resultLine: number[][] = []

        for (let index = 1; index < sourceLine.length; index++) {
            const prevPoint = sourceLine[index - 1]
            const nextPoint = sourceLine[index]

            const topCoefficient = index > 2 ? -0.2 : 0
            const botCoefficient = index < sourceLine.length - 2 ? 1.2 : 1

            const deltaX = nextPoint[0] - prevPoint[0]
            const deltaY = nextPoint[1] - prevPoint[1]

            resultLine.push([prevPoint[0] + deltaX * 0.25, prevPoint[1] + deltaY * topCoefficient])
            resultLine.push([prevPoint[0] + deltaX * 0.5, prevPoint[1] + deltaY * 0.5])
            resultLine.push([prevPoint[0] + deltaX * 0.75, prevPoint[1] + deltaY * botCoefficient])
        }

        resultLine.shift()
        resultLine.pop()

        return resultLine
    }

    private static createRopePoints(resultLine: number[][], symbolWidth: number, symbolHeight: number): PIXI.Point[] {
        const points: PIXI.Point[] = []
        const availablePoints = resultLine.length - 1
        const firstPoint = resultLine[0]
        const lastPoint = resultLine[resultLine.length - 1]

        for (let index = 0; index < availablePoints; index += 3) {
            const p1 = resultLine[index]
            const p2 = resultLine[index + 1]
            const p3 = resultLine[index + 2]
            const p4 = resultLine[index + 3]

            if (!p4) {
                break
            }

            for (let t = 0; t < 1; t += 0.05) {
                const x =
                    Math.pow(1 - t, 3) * p1[0] +
                    3 * Math.pow(1 - t, 2) * t * p2[0] +
                    3 * (1 - t) * Math.pow(t, 2) * p3[0] +
                    Math.pow(t, 3) * p4[0]
                const y =
                    Math.pow(1 - t, 3) * p1[1] +
                    3 * Math.pow(1 - t, 2) * t * p2[1] +
                    3 * (1 - t) * Math.pow(t, 2) * p3[1] +
                    Math.pow(t, 3) * p4[1]
                points.push(new PIXI.Point(x * symbolWidth, y * symbolHeight))
            }
        }

        for (let t = 0; t < 0.5; t += 0.05) {
            points.unshift(new PIXI.Point((firstPoint[0] - t) * symbolWidth, firstPoint[1] * symbolHeight))
            points.push(new PIXI.Point((lastPoint[0] + t) * symbolWidth, lastPoint[1] * symbolHeight))
        }

        return points
    }
}
